import { ref } from "vue";
import { match } from "ts-pattern";
import type { SubmissionResponse, TicketPayload, FormError } from "@hubspot-ticket/shared";
import { emitAnalytics } from "../utils/analytics";
import { getCookie } from "../utils/cookies";
import { withRetry, RetryableError } from "../utils/retry";

export type StatusState =
  | { type: "idle"; heading: string; message: string }
  | { type: "loading"; heading: string; message: string }
  | { type: "success"; heading: string; message: string }
  | { type: "error"; heading: string; message: string };

const idleState: StatusState = {
  type: "idle",
  heading: "",
  message: ""
};

const transformToTicketPayload = (formData: Record<string, unknown>): TicketPayload => {
  return {
    ticket: {
      subject: String(formData.subject || ""),
      description: String(formData.message || formData.description || ""),
      category: String(formData.issue_category || formData.category || "")
    },
    contact: {
      email: String(formData.email || ""),
      firstName: formData.firstname || formData.firstName ? String(formData.firstname || formData.firstName) : undefined,
      lastName: formData.lastname || formData.lastName ? String(formData.lastname || formData.lastName) : undefined,
      phone: formData.phone ? String(formData.phone) : undefined
    },
    context: {
      pageUrl: window.location.href,
      hubspotUtk: getCookie("hubspotutk")
    }
  };
};

export const useTicketSubmit = () => {
  const status = ref<StatusState>(idleState);
  const isSubmitting = ref(false);
  const fieldErrors = ref<FormError[]>([]);

  const handleRetry = (attempt: number, maxAttempts: number) => {
    status.value = {
      type: "loading",
      heading: "Retrying submission",
      message: `Attempt ${attempt} of ${maxAttempts}...`
    };
  };

  const submit = async (payload: unknown, turnstileToken?: string) => {
    isSubmitting.value = true;
    fieldErrors.value = []; // Clear previous errors
    status.value = { type: "loading", heading: "Submitting ticket", message: "Please hold on." };
    try {
      const formData = payload as Record<string, unknown>;
      const ticketPayload = transformToTicketPayload(formData);

      const formId = import.meta.env.VITE_HUBSPOT_FORM_ID || "default";
      const headers: Record<string, string> = { "content-type": "application/json" };

      if (turnstileToken) {
        headers["x-turnstile-token"] = turnstileToken;
      }

      const response = await withRetry(
        async () => {
          const res = await fetch(`/api/forms/${formId}/submit`, {
            method: "POST",
            headers,
            body: JSON.stringify(ticketPayload)
          });

          // Throw RetryableError for 429 and 5xx errors to trigger retry
          if (res.status === 429 || res.status >= 500) {
            throw new RetryableError(`Server error: ${res.status}`, res.status);
          }

          return res;
        },
        {
          maxAttempts: 3,
          baseDelay: 1000,
          maxDelay: 10000
        },
        handleRetry
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Failed to submit ticket";
        try {
          const errorBody = JSON.parse(errorText);
          errorMessage = errorBody.message || errorMessage;
          // Extract field errors if available
          if (errorBody.errors && Array.isArray(errorBody.errors)) {
            fieldErrors.value = errorBody.errors;
          }
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const body = (await response.json()) as SubmissionResponse;

      // Store field errors from response
      if (body.errors && Array.isArray(body.errors)) {
        fieldErrors.value = body.errors;
      }

      status.value = match(body.status)
        .with("success", () => ({
          type: "success",
          heading: "Ticket submitted",
          message: "Our support team has received your details."
        }))
        .with("retry", () => ({
          type: "error",
          heading: "Temporary issue",
          message: "Please try again in a few moments."
        }))
        .otherwise(() => ({
          type: "error",
          heading: "Submission failed",
          message: body.message
        }));

      emitAnalytics("ticket_submitted", {
        status: body.status,
        message: body.message
      });
    } catch (error) {
      let message = "Something went wrong.";
      if (error instanceof Error) {
        message = error.message;
      }
      status.value = {
        type: "error",
        heading: "Unexpected error",
        message
      };
      emitAnalytics("ticket_failed", { reason: "exception" });
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    submit,
    status,
    isSubmitting,
    fieldErrors
  };
};
